import { faPen, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface StockUbicacion {
  codigo: string;
  tipo: string;
  tipoId?: string;
  stock: number;
  ubicacionId?: string;
}

interface StockListProps {
  data: StockUbicacion[];
  onAddStock?: () => void;
  onEditStock?: (row: StockUbicacion) => void;
  readOnly?: boolean;
}

// Vista tipo card (ya no se usa con la tabla simple, se deja por si se reutiliza)

export const StockList: React.FC<StockListProps> = ({ data, onAddStock, onEditStock, readOnly = false }) => {
  const { t } = useTranslation('createEditProductsModule');

  return (
    <>
      <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 relative">
        <div className="col-span-1 md:col-span-2">
          <h2 className="text-lg font-semibold leading-tight mb-1">{t('sections.stock_title')}</h2>
          <p className="text-sm">{t('sections.stock_desc')}</p>
        </div>
        <div className="col-span-1 md:col-span-4 !mt-0">
          {!readOnly && (
            <div>
              <Button
                variant="outline"
                size="default"
                className="h-9 px-4 ml-auto absolute top-4 right-0 sm:relative sm:top-auto sm:right-auto"
                onClick={onAddStock}
              >
                <FontAwesomeIcon icon={faPlus} className="!w-4 !h-4" />
                <span className="hidden sm:inline-block">{t('sections.stock_agregar')}</span>
              </Button>
            </div>
          )}
          <div className="mt-2">
            {data.length > 0 && (
              <div className="w-full rounded-md overflow-hidden mt-4 sm:border sm:border-card-border">
                {/* Header como grid */}
                <div className="grid-cols-12 text-sm border-b border-card-border py-2 px-3 hidden sm:grid">
                  <div className="col-span-5 font-medium">{t('sections.stock_codigo')}</div>
                  <div className="col-span-4 font-medium">{t('sections.stock_tipo')}</div>
                  <div className="col-span-2 font-medium">{t('sections.stock_stock')}</div>
                  <div className="col-span-1 font-medium" />
                </div>

                {/* Filas */}
                <div>
                  {data.map((item) => {
                    let badgeClass = 'border-0';
                    if (item.tipoId === '2') {
                      badgeClass += ' text-badge-status-3 bg-card-border light:bg-badge-bg-status-3';
                    } else if (item.tipoId === '1') {
                      badgeClass += ' text-badge-status-2 bg-card-border light:bg-badge-bg-status-2';
                    }
                    return (
                      <div
                        key={item.ubicacionId || item.codigo}
                        className="border-b border-card-border py-3 sm:px-3 sm:grid sm:grid-cols-12 last:border-b-0"
                      >
                        {/* Código en su propia fila en mobile; columna en desktop */}
                        <div className="text-sm truncate font-medium sm:col-span-5">
                          <span className="text-lg sm:text-base font-medium sm:font-normal">{item.codigo}</span>
                        </div>

                        {/* Fila secundaria compacta en mobile (tipo, stock, acción) */}
                        <div className="flex items-center gap-3 mt-2 sm:hidden">
                          <Badge variant="outline" className="text-xs">
                            Stock: {item.stock}
                          </Badge>
                          <Badge variant="outline" className={badgeClass}>
                            {item.tipo}
                          </Badge>
                          {!readOnly && (
                            <button
                              type="button"
                              className="p-1 text-icons"
                              title="Editar"
                              onClick={() => onEditStock?.(item)}
                              data-testid={`edit-stock-${item.ubicacionId || item.codigo}`}
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </button>
                          )}
                        </div>

                        {/* Columnas en desktop (alineadas al header) */}
                        <div className="hidden sm:block sm:col-span-4">
                          <Badge variant="outline" className={badgeClass}>
                            {item.tipo}
                          </Badge>
                        </div>
                        <div className="hidden sm:block sm:col-span-2">
                          <span className="font-medium">{item.stock}</span>
                        </div>
                        {!readOnly && (
                          <div className="hidden sm:flex sm:col-span-1 justify-end">
                            <button
                              type="button"
                              className="p-1 text-icons"
                              title="Editar"
                              onClick={() => onEditStock?.(item)}
                              data-testid={`edit-stock-${item.ubicacionId || item.codigo}`}
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {data.length === 0 && (
        <div className="flex flex-col items-center justify-center text-center py-12 min-h-[55vh]">
          <img
            src="/imagen/empty-tables/empty-product-stock-light.svg"
            alt="Sin stock"
            className="w-24 h-24 mb-4 opacity-50"
          />
          <h3 className="text-lg font-medium text-title mb-2 whitespace-nowrap">{t('empty.title')}</h3>
          <p className="text-subtext w-[200px]">{t('empty.description')}</p>
        </div>
      )}
    </>
  );
};
